import { forwardRef, useImperativeHandle, useMemo, useRef } from "react";
import moment from "moment-timezone";
import messages from "messages";
// hooks
import { useCalendarTime } from "../../../../../../contexts/CalendarTimeContext";
// components
import Days from "../days";
import ScheduleDateRange from "./components/schedule-date-range";
// styles
import styles from "./AvailableTimePeriodEditor.module.scss";
// utils
import getClasses from "utils/getClasses";
import {
  isValidDateRange,
  isValidIntervals,
} from "../../utils/isValidParsedAvailableTimePeriod";
import { AmplitudePageName } from "utils/amplitude";

type Props = {
  value: ParsedAvailableTimePeriod;
  onApply: (arg: ParsedAvailableTimePeriod) => void;
  hidden?: boolean;
  onValidityChange?: (arg: boolean | undefined) => void;
  analyticsPageName: AmplitudePageName;
};

const ID = "weekly-hours";

/**
 * renders UI for editing schedule
 */
const ForwardedAvailableTimePeriodEditor = forwardRef<
  Pick<HTMLFormElement, "checkValidity" | "reset">,
  Props
>(function AvailableTimePeriodEditor(
  { value, onApply, hidden, onValidityChange, analyticsPageName },
  ref,
) {
  const { currentCalendarTimezone } = useCalendarTime();
  const { startDateValue, weekStartDateValue } = useMemo(
    () => ({
      startDateValue: +new Date(value.start_date),
      weekStartDateValue: moment(value.start_date)
        .tz(currentCalendarTimezone)
        .startOf("week")
        .valueOf(),
    }),
    [value.start_date, currentCalendarTimezone],
  );

  const inputRefs = useRef<
    (Pick<HTMLFormElement, "checkValidity" | "reset"> | null)[]
  >([]);

  const validityRef = useRef<(boolean | undefined)[]>([]);

  useImperativeHandle(ref, () => ({
    checkValidity: () => {
      const validities = inputRefs.current.map((el) => {
        if (!el) {
          return true;
        }

        return el.checkValidity();
      });

      // additionally, should check if no valid weekly hours
      if (!isValidIntervals(value)) {
        validities[1] = false;
      }
      const res = validities.every((el) => el !== false);

      validityRef.current = validities;

      return res;
    },
    reset: () => {
      validityRef.current = [undefined, undefined];
      inputRefs.current.forEach((el) => {
        if (!el) {
          return true;
        }
        return el.reset();
      });
      return onValidityChange && onValidityChange(undefined);
    },
  }));
  return (
    <div
      className={getClasses(
        hidden && styles["available-time-period-editor--hidden"],
      )}
    >
      {/* TODO: decide whether we have no valid weekly hours, overlapping date range to be displayed here */}
      {/* {noValidHours && <AutoFocusAlert error={messages.noValidHours} />} */}
      {/* select available days */}
      <ScheduleDateRange
        ref={(el) => {
          inputRefs.current[0] = el;
        }}
        timezone={currentCalendarTimezone}
        value={value}
        onChange={(nextValue) => {
          onApply(nextValue);
          if (validityRef.current[0] === false) {
            const res = isValidDateRange(nextValue);

            validityRef.current[0] = res;
            if (res) {
              if (validityRef.current.every((el) => el !== false)) {
                return onValidityChange && onValidityChange(true);
              }
            }
          }
        }}
      />
      {/* set up weekly hours */}
      <div className={styles["available-time-period-editor__hours"]}>
        <label
          className={styles["available-time-period-editor__label"]}
          htmlFor={ID}
        >
          {messages.setUpWeeklyHours}
        </label>
        <Days
          ref={(el) => {
            inputRefs.current[1] = el;
          }}
          id={ID}
          timezone={currentCalendarTimezone}
          weekStartDateValue={weekStartDateValue}
          startDateValue={startDateValue}
          values={value.intervals}
          onApply={(intervals) => {
            const nextValue = { ...value, intervals };
            onApply(nextValue);
            if (validityRef.current[1] === false) {
              const res = isValidIntervals(nextValue);

              validityRef.current[1] = res;
              if (res) {
                if (validityRef.current.every((el) => el !== false)) {
                  return onValidityChange && onValidityChange(true);
                }
              }
            }
          }}
        />
      </div>
    </div>
  );
});

export default ForwardedAvailableTimePeriodEditor;
